/**
 * Validation progressive du formulaire de contact (progressive enhancement).
 * La validation serveur reste la référence ; ce module améliore l'expérience
 * en signalant les erreurs côté client de manière accessible (aria-invalid,
 * messages liés via aria-describedby) sans bloquer l'envoi en cas d'échec JS.
 */

interface FieldRule {
  readonly validate: (value: string, field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => boolean;
  readonly message: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getErrorElement(field: Element): HTMLElement | null {
  const id = field.getAttribute('aria-describedby');
  if (!id) {
    return null;
  }
  // aria-describedby peut référencer plusieurs ids ; on prend l'élément d'erreur.
  const errorId = id.split(' ').find((candidate) => candidate.endsWith('-error'));
  return errorId ? document.getElementById(errorId) : null;
}

function setFieldError(
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  message: string | null
): void {
  const errorEl = getErrorElement(field);
  if (message) {
    field.setAttribute('aria-invalid', 'true');
    if (errorEl) {
      errorEl.textContent = message;
    }
  } else {
    field.removeAttribute('aria-invalid');
    if (errorEl) {
      errorEl.textContent = '';
    }
  }
}

function buildRule(field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): FieldRule | null {
  const required = field.hasAttribute('required');
  const type = field.getAttribute('type');

  if (type === 'email') {
    return {
      validate: (value) => (value.length === 0 ? !required : EMAIL_PATTERN.test(value)),
      message: 'Veuillez saisir une adresse e-mail valide.',
    };
  }

  if (field instanceof HTMLInputElement && field.type === 'checkbox') {
    return {
      validate: () => field.checked,
      message: field.dataset['errorMessage'] ?? 'Ce champ est requis.',
    };
  }

  if (required) {
    return {
      validate: (value) => value.trim().length > 0,
      message: field.dataset['errorMessage'] ?? 'Ce champ est requis.',
    };
  }

  return null;
}

export function initContactForm(): void {
  const form = document.querySelector<HTMLFormElement>('[data-contact-form]');
  if (!form) {
    return;
  }

  const fields = Array.from(
    form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[name], textarea[name], select[name]'
    )
  ).filter((field) => field.name !== 'Website' && field.type !== 'hidden');

  const validateField = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): boolean => {
    const rule = buildRule(field);
    if (!rule) {
      return true;
    }
    const valid = rule.validate(field.value, field);
    setFieldError(field, valid ? null : rule.message);
    return valid;
  };

  fields.forEach((field) => {
    field.addEventListener('blur', () => validateField(field));
    field.addEventListener('input', () => {
      if (field.getAttribute('aria-invalid') === 'true') {
        validateField(field);
      }
    });
  });

  form.addEventListener('submit', (event: SubmitEvent) => {
    const invalidFields: Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = [];

    fields.forEach((field) => {
      if (!validateField(field)) {
        invalidFields.push(field);
      }
    });

    const allValid = invalidFields.length === 0;
    const firstInvalid = invalidFields[0] ?? null;

    if (!allValid) {
      event.preventDefault();
      firstInvalid?.focus();
    }
  });
}
